import { useCallback, useEffect, useRef, useState } from "react";

const SR = typeof window !== "undefined" ? window.SpeechRecognition || window.webkitSpeechRecognition : null;
const synth = typeof window !== "undefined" ? window.speechSynthesis : null;

// Voice INPUT via the Web Speech API (graceful when unsupported).
export function useSpeechInput({ onResult, lang } = {}) {
  const supported = !!SR;
  const [listening, setListening] = useState(false);
  const recRef = useRef(null);

  const stop = useCallback(() => {
    try { recRef.current?.stop(); } catch {}
    setListening(false);
  }, []);

  const start = useCallback(() => {
    if (!supported) return;
    try {
      const rec = new SR();
      rec.lang = lang || navigator.language || "en-US";
      rec.interimResults = true;
      rec.continuous = false;
      rec.onresult = (e) => {
        let text = "";
        for (let i = 0; i < e.results.length; i += 1) text += e.results[i][0].transcript;
        onResult?.(text, e.results[e.results.length - 1].isFinal);
      };
      rec.onerror = () => setListening(false);
      rec.onend = () => setListening(false);
      recRef.current = rec;
      rec.start();
      setListening(true);
    } catch {
      setListening(false);
    }
  }, [supported, lang, onResult]);

  useEffect(() => () => { try { recRef.current?.abort?.(); } catch {} }, []);
  return { supported, listening, start, stop };
}

function stripForSpeech(md = "") {
  return md
    .replace(/```[\s\S]*?```/g, " code block ")
    .replace(/`([^`]+)`/g, "$1")
    .replace(/[#>*_~|]/g, " ")
    .replace(/\[(.*?)\]\((.*?)\)/g, "$1")
    .replace(/\s+/g, " ")
    .trim();
}

// Voice OUTPUT (text-to-speech).
export function useSpeechOutput() {
  const supported = !!synth;
  const [speaking, setSpeaking] = useState(false);

  const stop = useCallback(() => {
    try { synth?.cancel(); } catch {}
    setSpeaking(false);
  }, []);

  const speak = useCallback((text) => {
    if (!supported || !text) return;
    try {
      synth.cancel();
      const u = new SpeechSynthesisUtterance(stripForSpeech(text));
      u.lang = navigator.language || "en-US";
      u.rate = 1;
      u.onend = () => setSpeaking(false);
      u.onerror = () => setSpeaking(false);
      setSpeaking(true);
      synth.speak(u);
    } catch {
      setSpeaking(false);
    }
  }, [supported]);

  useEffect(() => () => { try { synth?.cancel(); } catch {} }, []);
  return { supported, speaking, speak, stop };
}
